// Navbar.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { FiFolder, FiActivity, FiBookOpen, FiBarChart2, FiSettings, FiLogOut, FiChevronDown } from 'react-icons/fi';
import './Navbar.css';
import logo from '../../assets/logo.png';
import {
  ROLE_EXPERT,
  ROLE_SUPERVISEUR,
  ROLE_SUPER_ADMIN,
  isClientOrgUser as isClientOrgUserFn,
  isStaffUser as isStaffUserFn,
  getOrganisationName,
} from '../../utils/session';

const ROLE_LABELS = {
  [ROLE_SUPER_ADMIN]: 'Super admin',
  [ROLE_SUPERVISEUR]: 'Superviseur',
  [ROLE_EXPERT]: 'Expert',
};

const Navbar = () => {
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [statsRmaAccess, setStatsRmaAccess] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [todayValidatedCount, setTodayValidatedCount] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const objectiveTotal = 100;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedRole = localStorage.getItem('user_role');
    const storedStatsRma = localStorage.getItem('stats_organisation') === 'true';
    setIsStaff(isStaffUserFn());
    if (storedUsername) setUsername(storedUsername);
    if (storedRole) setRole(storedRole);
    setStatsRmaAccess(storedStatsRma);
    setMenuOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const fetchCount = async () => {
      const token = localStorage.getItem('access_token');
      // Compteur de production : il n'a de sens que côté staff.
      if (isClientOrgUserFn()) return;
      if (!token) return;

      try {
        const res = await fetch('/api/stats/count-validated-today', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setTodayValidatedCount(data.count ?? 0);
      } catch (err) {
        console.error('Erreur count navbar:', err);
      }
    };

    fetchCount();
  }, []);

  // Ombre renforcée dès que la page défile
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Menu utilisateur : clic à l'extérieur ou Échap
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  // Échap referme le menu mobile
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const path = location.pathname.toLowerCase();
  const isActive = (to, extra = []) =>
    [to, ...extra].some((p) => path === p || path.startsWith(`${p}/`));

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const isLoginPage = location.pathname === '/';
  const initial = (username || '?').charAt(0).toUpperCase();
  const organisationName = getOrganisationName();

  const validatedCount = todayValidatedCount ?? 0;
  const objectivePercentRaw = Math.max(
    0,
    Math.round((validatedCount / objectiveTotal) * 100)
  );
  const objectiveRingPercent = Math.min(100, objectivePercentRaw);

  return (
    <nav className={`navbar${menuOpen ? ' navbar--open' : ''}${scrolled ? ' navbar--scrolled' : ''}`} aria-label="Navigation principale">
      <Link to="/dashboard" className="logo-link">
        <div className="logo">
          <img src={logo} alt="Logo" />
        </div>
      </Link>

      {!isLoginPage && (
        <button
          type="button"
          className="navbar-toggle"
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      )}

      {!isLoginPage && (
        <div className="nav-links">
          <Link to="/dashboard" className={`nav-item${isActive('/dashboard', ['/result-page']) ? ' is-active' : ''}`} aria-current={isActive('/dashboard', ['/result-page']) ? 'page' : undefined}>
            <FiFolder aria-hidden="true" /> Liste des Dossiers
          </Link>

          {(role === ROLE_SUPER_ADMIN) && (
            <Link to="/test-apis" className={`nav-item${isActive('/test-apis') ? ' is-active' : ''}`} aria-current={isActive('/test-apis') ? 'page' : undefined}>
              <FiActivity aria-hidden="true" /> Détection Temps Réel
            </Link>
          )}

          {(role === ROLE_SUPER_ADMIN || (role === ROLE_EXPERT && isStaff)) && (
            <Link to="/Documentation" className={`nav-item${isActive('/documentation') ? ' is-active' : ''}`} aria-current={isActive('/documentation') ? 'page' : undefined}>
              <FiBookOpen aria-hidden="true" /> Documentation
            </Link>
          )}

          {(statsRmaAccess || role === ROLE_SUPER_ADMIN) && (
            <Link to="/stats" className={`nav-item${isActive('/stats') ? ' is-active' : ''}`} aria-current={isActive('/stats') ? 'page' : undefined}>
              <FiBarChart2 aria-hidden="true" /> Stats
            </Link>
          )}

          {role === ROLE_SUPER_ADMIN && (
            <Link to="/admindash" className={`nav-item${isActive('/admindash') ? ' is-active' : ''}`} aria-current={isActive('/admindash') ? 'page' : undefined}>
              <FiSettings aria-hidden="true" /> Gestion
            </Link>
          )}
        </div>
      )}

      {!isLoginPage && (
        <div className="nb-user" ref={userMenuRef}>
          <button
            type="button"
            className={`nb-user-btn${userMenuOpen ? ' is-open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            onClick={() => setUserMenuOpen((o) => !o)}
          >
            <span className="user-avatar" aria-hidden="true">{initial}</span>
            <span className="user-text">
              <span className="user-name">{username}</span>
              {ROLE_LABELS[role] && <span className="user-role">{ROLE_LABELS[role]}</span>}
            </span>
            <FiChevronDown className="nb-user-chev" aria-hidden="true" />
          </button>

          {userMenuOpen && (
            <div className="nb-user-panel" role="menu">
              <div className="nb-user-head">
                <span className="user-avatar user-avatar--lg" aria-hidden="true">{initial}</span>
                <div className="nb-user-id">
                  <span className="nb-user-fullname">{username}</span>
                  {ROLE_LABELS[role] && <span className="nb-user-sub">{ROLE_LABELS[role]}</span>}
                  {organisationName && <span className="nb-user-sub">{organisationName}</span>}
                </div>
              </div>
              <button type="button" role="menuitem" className="nb-user-logout" onClick={handleLogout}>
                <FiLogOut aria-hidden="true" /> Déconnexion
              </button>
            </div>
          )}
        </div>
      )}

      {/* Carte utilisateur affichée dans le menu burger (mobile) */}
      {!isLoginPage && (
        <div className="nb-mobile-user">
          <div className="username">
            <span className="user-avatar" aria-hidden="true">{initial}</span>
            <span className="user-text">
              <span className="user-name">{username}</span>
              {ROLE_LABELS[role] && <span className="user-role">{ROLE_LABELS[role]}</span>}
            </span>
          </div>
          <button type="button" onClick={handleLogout} className="logout-btn">
            <FiLogOut aria-hidden="true" />
            <span>Déconnexion</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;